import React, { Suspense, useContext } from 'react';
import { Outlet, UNSAFE_DataRouterStateContext, UNSAFE_RouteContext } from 'react-router-dom';

import { RouteFallback } from '@components/common/routeLoading';

/**
 * Suspense boundary for a layout route's <Outlet />.
 *
 * APL-446: under React 18, navigating while a lazy route chunk is still loading
 * leaves a shared, un-keyed boundary stuck on its fallback and the destination
 * never renders — react-router v7's startTransition does not fix it on its own.
 * Remounting the boundary per navigation does.
 *
 * The key is the id of the match DIRECTLY BELOW this boundary, never the leaf.
 * Keying on the leaf remounts everything under the boundary on every
 * navigation, which would tear down the app shell and each section's store
 * provider — the very state those layouts exist to keep alive. Keyed per level,
 * a boundary only remounts when the branch it owns actually changes:
 *
 *   RootLayout          keys on the shell vs a pre-auth page
 *   app shell           keys on which section
 *   section layout      keys on which page in that section
 *
 * Depth comes from the router's own match context rather than a counted
 * provider: routes that declare a path and a `handle` but no `element` — the
 * bot management, event blocking, data mapper and tracking plan section roots —
 * occupy a match slot without rendering anything, so a counter would drift
 * shallow and a boundary could end up keyed on itself, silently disabling it.
 *
 * Both contexts are read directly rather than through `useMatches()`, which
 * throws outside a data router: this renders inside the app shell and every
 * section layout, and those are mounted by tests and stories under a plain
 * <BrowserRouter>. Verified against react-router 7.18. If either context is
 * missing or the index falls outside the match chain — the latter happens
 * inside a descendant <Routes>, whose matches the data router cannot see — the
 * boundary goes un-keyed. That loses the remount, never the render, so the
 * failure direction is a stale boundary rather than a torn-down subtree.
 */
export const RouteBoundary: React.FC = () => {
  const dataRouterState = useContext(UNSAFE_DataRouterStateContext);
  // Matches on the route context are those consumed down to and including the
  // route this boundary renders in, so its length indexes the next one.
  const { matches: consumed } = useContext(UNSAFE_RouteContext);

  const matches = dataRouterState?.matches;
  const childRouteId =
    matches && consumed.length < matches.length ? matches[consumed.length]?.route.id : undefined;

  return (
    <Suspense key={childRouteId} fallback={<RouteFallback />}>
      <Outlet />
    </Suspense>
  );
};
